import { BaseApiClient, QueryParams } from './core';
import type { ActivityLogListResponse, ActivityTypeInfo, OrganizationInvitation, OrganizationInvitationListResponse, OrganizationMember, OrganizationMemberListResponse, OrganizationRole } from '@/types/org';
import type { QuarterInReviewData, YearInReviewData } from '@/types/share';

export class OrgApiClient extends BaseApiClient {
  // Organization Members
  // ==========================================

  async getMembers(orgId: string, page = 1, pageSize = 50): Promise<OrganizationMemberListResponse> {
    const { data } = await this.client.get<OrganizationMemberListResponse>('/members', {
      params: { orgId, page, pageSize },
    });
    return data;
  }
  async inviteMember(
    orgId: string,
    githubUsername: string,
    role: OrganizationRole = 'member'
  ): Promise<OrganizationInvitation> {
    // org_id is a QUERY param on the backend (POST /members/invite?org_id=...),
    // not a body field. It must go in `params`, like getInvitations below -
    // sending it in the body leaves the required query param missing and the
    // backend returns 422 for every invite.
    const { data } = await this.client.post<OrganizationInvitation>(
      '/members/invite',
      { githubUsername, role },
      { params: { orgId } }
    );
    return data;
  }
  async getInvitations(orgId: string): Promise<OrganizationInvitationListResponse> {
    const { data } = await this.client.get<OrganizationInvitationListResponse>('/members/invitations', {
      params: { orgId },
    });
    return data;
  }
  async revokeInvitation(orgId: string, invitationId: string): Promise<void> {
    await this.client.delete(`/members/invitations/${invitationId}`, {
      params: { orgId },
    });
  }
  async changeMemberRole(orgId: string, memberId: string, role: OrganizationRole): Promise<OrganizationMember> {
    const { data } = await this.client.patch<OrganizationMember>(`/members/${memberId}/role`, { role }, {
      params: { orgId },
    });
    return data;
  }
  async removeMember(orgId: string, memberId: string): Promise<void> {
    await this.client.delete(`/members/${memberId}`, {
      params: { orgId },
    });
  }
  async getMyMembership(orgId: string): Promise<OrganizationMember> {
    const { data } = await this.client.get<OrganizationMember>('/members/me', {
      params: { orgId },
    });
    return data;
  }
  // ==========================================
  // Organization Activity Logs
  // ==========================================

  async getActivityLogs(
    orgId: string,
    activityType?: string,
    userId?: string,
    page = 1,
    pageSize = 50
  ): Promise<ActivityLogListResponse> {
    const params: QueryParams ={ orgId, page, pageSize };
    if (activityType) params.activityType = activityType;
    if (userId) params.userId = userId;

    const { data } = await this.client.get<ActivityLogListResponse>('/activity', { params });
    return data;
  }
  async getActivityTypes(): Promise<ActivityTypeInfo[]> {
    const { data } = await this.client.get<ActivityTypeInfo[]>('/activity/types');
    return data;
  }
  // ==========================================
  // Year/Quarter Review
  // ==========================================

  async getYearReview(orgId: string, year: number, excludeBots = true): Promise<YearInReviewData> {
    const { data } = await this.client.get<YearInReviewData>(
      `/reviews/year/${year}`,
      { params: { orgId: orgId, excludeBots: excludeBots } }
    );
    return data;
  }
  async getQuarterReview(orgId: string, year: number, quarter: number, excludeBots = true): Promise<QuarterInReviewData> {
    const { data } = await this.client.get<QuarterInReviewData>(
      `/reviews/quarter/${year}/${quarter}`,
      { params: { orgId: orgId, excludeBots: excludeBots } }
    );
    return data;
  }
  async exportReviewCSV(
    orgId: string,
    reviewType: 'year' | 'quarter',
    period: string,
    excludeBots = true
  ): Promise<Blob> {
    const [yearOrYear, quarterNum] = period.split('-Q');
    const endpoint = reviewType === 'year'
      ? `/reviews/export/csv/year/${yearOrYear}`
      : `/reviews/export/csv/quarter/${yearOrYear}/${quarterNum}`;

    const { data } = await this.client.get(endpoint, {
      params: { orgId: orgId, excludeBots: excludeBots },
      responseType: 'blob',
    });
    return data;
  }
  async exportReviewPDF(
    orgId: string,
    reviewType: 'year' | 'quarter',
    period: string,
    excludeBots = true
  ): Promise<Blob> {
    const endpoint = reviewType === 'year'
      ? `/reviews/export/pdf/year/${period}`
      : `/reviews/export/pdf/quarter/${period}`;

    const { data } = await this.client.get(endpoint, {
      params: { orgId: orgId, excludeBots: excludeBots },
      responseType: 'blob',
    });
    return data;
  }
  async getReviewSharePreview(
    orgId: string,
    reviewType: 'year' | 'quarter',
    period: string
  ) {
    const [yearOrYear, quarterNum] = period.split('-Q');
    const endpoint = reviewType === 'year'
      ? `/reviews/share-preview/year/${yearOrYear}`
      : `/reviews/share-preview/quarter/${yearOrYear}/${quarterNum}`;

    const { data } = await this.client.get(endpoint, {
      params: { orgId: orgId },
    });
    return data;
  }
}

export const orgApi = new OrgApiClient();
